import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, Users, CheckCircle, XCircle, TrendingUp, Calendar, Download, RefreshCw } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [topCampaigns, setTopCampaigns] = useState([]);
  const [weekData, setWeekData] = useState([]);
  const [syncState, setSyncState] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, historyRes, performanceRes, campaignsRes, trendRes, syncRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/history?limit=20`),
        axios.get(`${API}/analytics/performance`),
        axios.get(`${API}/analytics/top-campaigns`),
        axios.get(`${API}/analytics/weekly-trend`),
        axios.get(`${API}/resend/sync`)
      ]);
      setStats(statsRes.data);
      setHistory(historyRes.data);
      setPerformance(performanceRes.data);
      setTopCampaigns(campaignsRes.data.top_campaigns || []);
      setWeekData(trendRes.data.trend || []);
      setSyncState(syncRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Panel verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Resend'deki açılma/tıklama/bounce durumlarını ve gelen cevapları elle çeker.
  const runResendSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/resend/sync`);
      const { events, received } = res.data;
      toast.success(
        `Resend senkronlandı — ${events.updated} gönderim güncellendi, ${received.created} yeni cevap`
      );
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Resend senkronu başarısız');
    } finally {
      setSyncing(false);
    }
  };

  const exportDashboardCSV = () => {
    const csvData = [
      ['Metrik', 'Değer'],
      ['Toplam Şablon', stats?.total_templates || 0],
      ['Toplam Alıcı', stats?.total_recipients || 0],
      ['Gönderilen Email', stats?.total_sent || 0],
      ['Başarısız', stats?.total_failed || 0],
      ['Başarı Oranı', `${stats?.success_rate || 0}%`],
      ['Açılma Oranı', `${stats?.open_rate || 0}%`],
      ['Tıklama Oranı', `${stats?.click_rate || 0}%`],
      ['Bugün Gönderilen', stats?.today_sent || 0],
      ['Bu Hafta Gönderilen', stats?.week_sent || 0]
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dashboard_rapor_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('📊 Dashboard raporu indirildi!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6366f1]"></div>
      </div>
    );
  }

  const kpiCards = [
    { label: 'Toplam Şablon', value: stats?.total_templates || 0, icon: Mail, color: '#6366f1' },
    { label: 'Toplam Alıcı', value: stats?.total_recipients || 0, icon: Users, color: '#10b981' },
    { label: 'Gönderilen Email', value: stats?.total_sent || 0, icon: CheckCircle, color: '#6366f1' },
    { label: 'Başarısız', value: stats?.total_failed || 0, icon: XCircle, color: '#ef4444' },
  ];

  const pieData = [
    { name: 'Gönderildi', value: stats?.total_sent || 0, color: '#10b981' },
    { name: 'Başarısız', value: stats?.total_failed || 0, color: '#ef4444' },
  ];

  const hasWeekData = weekData.some((d) => d.sent > 0 || d.failed > 0);
  const lastSyncLabel = syncState?.last_sync?.synced_at
    ? new Date(syncState.last_sync.synced_at).toLocaleString('tr-TR')
    : null;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0f172a] tracking-tight">Kontrol Paneli</h1>
          <p className="text-sm text-[#64748b] mt-1">Email pazarlama kampanyalarınızın özeti</p>
        </div>
        <div className="flex items-center gap-2">
          {syncState?.configured && (
            <Button onClick={runResendSync} variant="outline" size="sm" disabled={syncing}>
              <RefreshCw size={15} className={`mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Senkronlanıyor...' : "Resend'den Güncelle"}
            </Button>
          )}
          <Button onClick={exportDashboardCSV} variant="outline" size="sm">
            <Download size={15} className="mr-2" />
            Rapor İndir (CSV)
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((kpi, index) => (
          <div
            key={index}
            data-testid={`kpi-card-${kpi.label.toLowerCase().replace(' ', '-')}`}
            className="bg-white rounded-xl border border-[#e2e8f0] p-4 card-hover"
          >
            <div className="mb-3">
              <div className="inline-flex p-2 rounded-lg" style={{ backgroundColor: `${kpi.color}15` }}>
                <kpi.icon size={20} color={kpi.color} strokeWidth={1.5} />
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-[#64748b] mb-1">{kpi.label}</p>
              <p className="text-2xl font-bold text-[#0f172a]">{kpi.value.toLocaleString()}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Email Analytics - Resend Metrikleri */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-4 sm:p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500 rounded-lg">
              <TrendingUp size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#0f172a]">Email Performans Metrikleri</h2>
              <p className="text-xs text-[#64748b]">
                {lastSyncLabel
                  ? `Resend ile son senkron: ${lastSyncLabel}`
                  : 'Gönderim geçmişinden hesaplanır'}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {/* Açılma Oranı */}
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[#64748b]">Açılma Oranı</p>
              <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                stats?.open_rate >= 20 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                Hedef: 20%+
              </span>
            </div>
            <p className="text-2xl font-bold text-[#0f172a] mb-1">
              {stats?.open_rate !== null && stats?.open_rate !== undefined ? `${stats.open_rate}%` : '-'}
            </p>
            <p className="text-xs text-[#94a3b8]">
              {stats?.total_sent > 0
                ? (stats?.open_rate ? 'Resend tracking aktif' : 'Henüz açılma yok')
                : 'Henüz veri yok'}
            </p>
          </div>

          {/* Tıklama Oranı */}
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[#64748b]">Tıklama Oranı</p>
              <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                stats?.click_rate >= 2 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                Hedef: 2%+
              </span>
            </div>
            <p className="text-2xl font-bold text-[#0f172a] mb-1">
              {stats?.click_rate !== null && stats?.click_rate !== undefined ? `${stats.click_rate}%` : '-'}
            </p>
            <p className="text-xs text-[#94a3b8]">
              {stats?.total_sent > 0
                ? (stats?.click_rate ? 'Resend tracking aktif' : 'Henüz tıklama yok')
                : 'Henüz veri yok'}
            </p>
          </div>

          {/* Teslimat Oranı */}
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[#64748b]">Teslimat Oranı</p>
              <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-bold">Hedef: 95%+</span>
            </div>
            <p className="text-2xl font-bold text-[#0f172a] mb-1">
              {stats?.total_sent > 0 ? Math.round((stats.total_sent / (stats.total_sent + stats.total_failed)) * 100) : '-'}%
            </p>
            <p className="text-xs text-[#94a3b8]">
              {stats?.total_sent > 0 ? `${stats.total_sent} / ${stats.total_sent + stats.total_failed}` : 'Henüz veri yok'}
            </p>
          </div>

          {/* Bounce Oranı */}
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[#64748b]">Bounce Oranı</p>
              <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                (stats?.bounce_rate || 0) <= 5 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                Max: 5%
              </span>
            </div>
            <p className="text-2xl font-bold text-[#0f172a] mb-1">
              {stats?.bounce_rate !== null && stats?.bounce_rate !== undefined ? `${stats.bounce_rate}%` : '0%'}
            </p>
            <p className="text-xs text-[#94a3b8]">
              {stats?.total_failed > 0 ? `${stats.total_failed} bounce` : 'Harika! Bounce yok'}
            </p>
          </div>

          {/* Spam Şikayet */}
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[#64748b]">Spam Şikayet</p>
              <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                (stats?.spam_rate || 0) <= 0.1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                Max: 0.1%
              </span>
            </div>
            <p className="text-2xl font-bold text-[#0f172a] mb-1">
              {stats?.spam_rate !== null && stats?.spam_rate !== undefined ? `${stats.spam_rate}%` : '-'}
            </p>
            <p className="text-xs text-[#94a3b8]">
              {stats?.total_sent > 0
                ? (stats?.spam_rate ? 'Şikayet var — inceleyin' : 'Şikayet yok')
                : 'Henüz veri yok'}
            </p>
          </div>
        </div>

        <div className="mt-4 p-4 bg-blue-100 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>💡 Not:</strong> Açılma ve tıklama oranları Resend Dashboard'da görüntülenebilir. 
            Daha detaylı analytics için{' '}
            <a 
              href="https://resend.com/emails" 
              target="_blank" 
              rel="noopener noreferrer"
              className="underline font-bold hover:text-blue-900"
            >
              Resend Dashboard
            </a>
            'u ziyaret edin.
          </p>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Weekly Trend — gerçek gönderim geçmişinden (eskiden sabit sayılardı) */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-[#e2e8f0] p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={20} className="text-[#6366f1]" />
            <h2 className="text-lg font-bold text-[#0f172a]">Son 7 Gün Gönderim Trendi</h2>
          </div>
          {hasWeekData ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={weekData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} allowDecimals={false} />
                <Tooltip
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.label || ''}
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                  }}
                />
                <Legend />
                <Line type="monotone" dataKey="sent" name="Gönderilen" stroke="#6366f1" strokeWidth={2.5} dot={{ fill: '#6366f1', r: 3 }} />
                <Line type="monotone" dataKey="opened" name="Açılan" stroke="#10b981" strokeWidth={2.5} dot={{ fill: '#10b981', r: 3 }} />
                <Line type="monotone" dataKey="failed" name="Başarısız" stroke="#ef4444" strokeWidth={2} dot={{ fill: '#ef4444', r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[260px] flex flex-col items-center justify-center text-center">
              <Calendar size={36} className="text-[#cbd5e1] mb-3" />
              <p className="text-sm font-medium text-[#0f172a]">Son 7 günde gönderim yok</p>
              <p className="text-xs text-[#64748b] mt-1">Kampanya gönderdiğinizde trend burada görünür</p>
            </div>
          )}
        </div>

        {/* Success Rate */}
        <div className="bg-white rounded-xl border border-[#e2e8f0] p-4 sm:p-5">
          <h2 className="text-lg font-bold text-[#0f172a] mb-4">Başarı Oranı</h2>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 text-center">
            <p className="text-2xl font-bold text-[#0f172a]">{stats?.success_rate || 0}%</p>
            <p className="text-sm text-[#64748b]">Genel Başarı Oranı</p>
          </div>
        </div>
      </div>

      {/* Hourly Performance */}
      {performance && performance.hourly_performance && performance.hourly_performance.length > 0 && (
        <div className="bg-white rounded-xl border border-[#e2e8f0] p-5">
          <h2 className="text-lg font-bold text-[#0f172a] mb-4">📊 Saatlik Performans</h2>
          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-900">
              <strong>🏆 En İyi Saat:</strong> {performance.best_hour}:00 - {performance.best_hour_opens} açılma
            </p>
            <p className="text-xs text-blue-700 mt-1">
              Email gönderme için optimize edilmiş zaman
            </p>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={performance.hourly_performance}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="hour" stroke="#64748b" label={{ value: 'Saat', position: 'insideBottom', offset: -5 }} />
              <YAxis stroke="#64748b" />
              <Tooltip />
              <Legend />
              <Bar dataKey="sent" fill="#6366f1" name="Gönderilen" />
              <Bar dataKey="opened" fill="#10b981" name="Açılan" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top Campaigns */}
      {topCampaigns && topCampaigns.length > 0 && (
        <div className="bg-white rounded-xl border border-[#e2e8f0] p-5">
          <h2 className="text-lg font-bold text-[#0f172a] mb-4">🎯 En Başarılı Kampanyalar</h2>
          <div className="space-y-3">
            {topCampaigns.map((campaign, idx) => (
              <div key={campaign.campaign_id} className="p-4 bg-gradient-to-r from-[#f8fafc] to-white rounded-lg border border-[#e2e8f0]">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '📧'}</span>
                    <span className="font-semibold text-[#0f172a]">{campaign.name || 'Kampanya'}</span>
                  </div>
                  <span className="text-xs text-[#64748b]">
                    {new Date(campaign.sent_at).toLocaleDateString('tr-TR')}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-[#64748b]">Alıcı</p>
                    <p className="text-lg font-bold text-[#0f172a]">{campaign.total_recipients}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748b]">Açılma</p>
                    <p className="text-lg font-bold text-[#10b981]">{campaign.open_rate}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748b]">Tıklama</p>
                    <p className="text-lg font-bold text-[#6366f1]">{campaign.click_rate}%</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="bg-white rounded-xl border border-[#e2e8f0] p-5">
        <div className="flex items-center gap-3 mb-6">
          <Calendar size={24} className="text-[#6366f1]" />
          <h2 className="text-lg font-bold text-[#0f172a]">Son Aktiviteler</h2>
        </div>
        {history.length === 0 && (
          <p className="text-sm text-[#64748b] py-6 text-center">
            Henüz gönderim yok. İlk kampanyanızı gönderdiğinizde burada listelenir.
          </p>
        )}
        <div className="space-y-2">
          {history.slice(0, 5).map((record) => (
            <div
              key={record.id}
              className="flex items-center justify-between gap-3 p-3 bg-[#f8fafc] rounded-lg border border-[#e2e8f0]"
            >
              <div className="flex items-center gap-3">
                {record.status === 'sent' ? (
                  <CheckCircle size={20} className="text-[#10b981]" />
                ) : (
                  <XCircle size={20} className="text-[#ef4444]" />
                )}
                <div>
                  <p className="font-medium text-[#0f172a]">{record.recipient_email}</p>
                  <p className="text-sm text-[#64748b]">{record.template_name}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-[#64748b]">
                  {new Date(record.sent_at).toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;